import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import './LandingPage.css';

interface FeaturedClient {
  id: number;
  name: string;
  logo_url: string;
  website_url: string | null;
}

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  cover_image_url: string;
  cover_image_alt: string;
  tags: string;
  author_name: string;
  published_at: string;
}

const LandingPage: React.FC = () => {
  useDocumentTitle('Monitor, Rank & Grow Your Digital Assets | CloudAyn', false);
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [clients, setClients] = useState<FeaturedClient[]>([]);
  const [posts, setPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchHomepageData = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
        const [homepageRes, blogRes] = await Promise.all([
          fetch(`${apiUrl}/v1/public/homepage`),
          fetch(`${apiUrl}/v1/public/blog?limit=3`),
        ]);
        const homepageData = await homepageRes.json();
        if (homepageData.status === 'success') {
          setClients(homepageData.data.featured_clients);
        }
        const blogData = await blogRes.json();
        if (blogData.status === 'success') {
          setPosts(blogData.data.posts || []);
        }
      } catch (error) {
        console.error('Failed to fetch homepage data:', error);
      }
    };
    fetchHomepageData();
  }, []);

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className={`navbar navbar-expand-lg navbar-light fixed-top ${scrolled ? 'navbar-scrolled' : ''}`}>
        <div className="container">
          <Link className="navbar-brand d-flex align-items-center" to="/">
            <div className="logo-icon">
              <span className="logo-text">C</span>
            </div>
            <span className="brand-name ms-2">CloudAyn</span>
          </Link>

          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNav"
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav ms-auto align-items-center">
              <li className="nav-item">
                <a className="nav-link" href="#features">Features</a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="#preview">Preview</a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="#contact">Contact</a>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/blog">Blog</Link>
              </li>
              <li className="nav-item ms-3">
                <button className="btn btn-outline-primary" onClick={() => navigate('/login')}>
                  Login
                </button>
              </li>
              <li className="nav-item ms-2">
                <button className="btn btn-primary" onClick={() => navigate('/signup')}>
                  Get Started
                </button>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Hero Section - Redesigned */}
      <section className="hero-section-new">
        <div className="hero-background">
          <div className="gradient-bg"></div>
          <div className="animated-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>
          </div>
        </div>

        <div className="container">
          <div className="row align-items-center min-vh-100 py-5">
            <div className="col-lg-6 hero-content">
              <div className="hero-badge animate-slide-up mb-3">
                <span className="badge-pill">All-in-One Platform</span>
              </div>
              <h1 className="hero-title animate-slide-up">
                Monitor, Rank &
                <span className="gradient-text"> Grow</span>
                <br />Your Digital Assets
              </h1>
              <p className="hero-subtitle animate-slide-up" style={{ animationDelay: '0.2s' }}>
                Real-time analytics, uptime monitoring, security scanning, and AI-powered SEO
                optimization — all in one powerful dashboard.
              </p>
              <div className="hero-buttons animate-slide-up" style={{ animationDelay: '0.4s' }}>
                <button className="btn btn-lg btn-primary" onClick={() => navigate('/signup')}>
                  Get Started
                  <i className="bi bi-arrow-right ms-2"></i>
                </button>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="hero-dashboard-preview animate-float">
                {/* Mini Live Visitors Preview */}
                <div className="mini-card live-card">
                  <div className="mini-card-header">
                    <span className="live-dot"></span>
                    <span>Live Visitors</span>
                  </div>
                  <div className="live-hub-mini">
                    <div className="hub-center">
                      <span className="count">247</span>
                      <span className="label">online</span>
                    </div>
                    <div className="visitor-dot" style={{ top: '15%', left: '20%' }}></div>
                    <div className="visitor-dot" style={{ top: '25%', right: '15%' }}></div>
                    <div className="visitor-dot" style={{ bottom: '20%', left: '25%' }}></div>
                    <div className="visitor-dot" style={{ bottom: '30%', right: '20%' }}></div>
                    <div className="visitor-dot" style={{ top: '50%', left: '10%' }}></div>
                  </div>
                </div>

                {/* Mini Stats Cards */}
                <div className="mini-stats-row">
                  <div className="mini-stat-card">
                    <i className="bi bi-people text-primary"></i>
                    <div className="stat-content">
                      <span className="stat-value">12.5K</span>
                      <span className="stat-label">Visitors</span>
                    </div>
                  </div>
                  <div className="mini-stat-card">
                    <i className="bi bi-eye text-success"></i>
                    <div className="stat-content">
                      <span className="stat-value">48.2K</span>
                      <span className="stat-label">Page Views</span>
                    </div>
                  </div>
                </div>

                {/* Mini Chart Preview */}
                <div className="mini-chart-card">
                  <div className="chart-header">
                    <span>Analytics Overview</span>
                    <span className="badge bg-success-subtle text-success">+24%</span>
                  </div>
                  <div className="mini-area-chart">
                    <svg viewBox="0 0 200 60" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3"/>
                          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0"/>
                        </linearGradient>
                      </defs>
                      <path d="M0,45 Q25,40 50,35 T100,25 T150,30 T200,15 L200,60 L0,60 Z" fill="url(#chartGradient)"/>
                      <path d="M0,45 Q25,40 50,35 T100,25 T150,30 T200,15" fill="none" stroke="#3b82f6" strokeWidth="2"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Preview Section */}
      <section id="preview" className="preview-section py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="section-title">See Your Dashboard in Action</h2>
            <p className="section-subtitle">
              Powerful tools designed to give you complete visibility into your digital presence
            </p>
          </div>

          {/* Live Analytics Preview */}
          <div className="preview-block mb-5">
            <div className="row align-items-center">
              <div className="col-lg-5 mb-4 mb-lg-0">
                <div className="preview-content">
                  <div className="preview-icon bg-primary-subtle">
                    <i className="bi bi-broadcast text-primary"></i>
                  </div>
                  <h3>Live Visitor Tracking</h3>
                  <p>Watch your visitors in real-time. See who's on your site right now, their location, device, and what pages they're viewing.</p>
                  <ul className="preview-features-list">
                    <li><i className="bi bi-check2"></i> Real-time visitor count</li>
                    <li><i className="bi bi-check2"></i> Geographic distribution</li>
                    <li><i className="bi bi-check2"></i> Device & browser breakdown</li>
                  </ul>
                </div>
              </div>
              <div className="col-lg-7">
                <div className="preview-dashboard-card live-dashboard">
                  <div className="preview-card-header">
                    <div className="window-dots">
                      <span></span><span></span><span></span>
                    </div>
                    <span className="header-title">Live Visitors Hub</span>
                    <div className="live-badge-header">
                      <span className="live-indicator"></span>
                      LIVE
                    </div>
                  </div>
                  <div className="preview-card-body">
                    <div className="live-hub-preview">
                      <svg className="connection-lines" viewBox="0 0 300 200">
                        <line x1="150" y1="100" x2="50" y2="40" stroke="#3b82f6" strokeWidth="1" strokeDasharray="4"/>
                        <line x1="150" y1="100" x2="250" y2="50" stroke="#10b981" strokeWidth="1" strokeDasharray="4"/>
                        <line x1="150" y1="100" x2="40" y2="150" stroke="#f59e0b" strokeWidth="1" strokeDasharray="4"/>
                        <line x1="150" y1="100" x2="260" y2="160" stroke="#3b82f6" strokeWidth="1" strokeDasharray="4"/>
                        <line x1="150" y1="100" x2="100" y2="180" stroke="#10b981" strokeWidth="1" strokeDasharray="4"/>
                        <line x1="150" y1="100" x2="200" y2="30" stroke="#f59e0b" strokeWidth="1" strokeDasharray="4"/>
                      </svg>
                      <div className="hub-center-large">
                        <div className="pulse-ring"></div>
                        <div className="pulse-ring delay-1"></div>
                        <div className="hub-core-large">
                          <span className="online-count-large">156</span>
                          <span className="online-label-large">online</span>
                        </div>
                      </div>
                      <div className="floating-visitor-preview" style={{ top: '15%', left: '12%' }}>
                        <div className="device-badge bg-primary"><i className="bi bi-laptop"></i></div>
                        <span className="flag-emoji">US</span>
                      </div>
                      <div className="floating-visitor-preview" style={{ top: '20%', right: '15%' }}>
                        <div className="device-badge bg-success"><i className="bi bi-phone"></i></div>
                        <span className="flag-emoji">UK</span>
                      </div>
                      <div className="floating-visitor-preview" style={{ bottom: '25%', left: '10%' }}>
                        <div className="device-badge bg-warning"><i className="bi bi-tablet"></i></div>
                        <span className="flag-emoji">DE</span>
                      </div>
                      <div className="floating-visitor-preview" style={{ bottom: '20%', right: '12%' }}>
                        <div className="device-badge bg-primary"><i className="bi bi-laptop"></i></div>
                        <span className="flag-emoji">IN</span>
                      </div>
                      <div className="floating-visitor-preview" style={{ top: '10%', left: '55%' }}>
                        <div className="device-badge bg-success"><i className="bi bi-phone"></i></div>
                        <span className="flag-emoji">CA</span>
                      </div>
                      <div className="floating-visitor-preview" style={{ bottom: '10%', right: '45%' }}>
                        <div className="device-badge bg-warning"><i className="bi bi-tablet"></i></div>
                        <span className="flag-emoji">AU</span>
                      </div>
                    </div>
                    <div className="live-stats-bar">
                      <div className="device-breakdown">
                        <span className="breakdown-item"><i className="bi bi-laptop text-primary"></i> Desktop: 89</span>
                        <span className="breakdown-item"><i className="bi bi-phone text-success"></i> Mobile: 52</span>
                        <span className="breakdown-item"><i className="bi bi-tablet text-warning"></i> Tablet: 15</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Analytics Dashboard Preview */}
          <div className="preview-block mb-5">
            <div className="row align-items-center flex-lg-row-reverse">
              <div className="col-lg-5 mb-4 mb-lg-0">
                <div className="preview-content">
                  <div className="preview-icon bg-success-subtle">
                    <i className="bi bi-graph-up text-success"></i>
                  </div>
                  <h3>Comprehensive Analytics</h3>
                  <p>Beautiful charts and reports that help you understand your visitors. Track trends, measure growth, and make data-driven decisions.</p>
                  <ul className="preview-features-list">
                    <li><i className="bi bi-check2"></i> Traffic trend analysis</li>
                    <li><i className="bi bi-check2"></i> Page performance metrics</li>
                    <li><i className="bi bi-check2"></i> Year-over-year comparisons</li>
                  </ul>
                </div>
              </div>
              <div className="col-lg-7">
                <div className="preview-dashboard-card analytics-dashboard">
                  <div className="preview-card-header">
                    <div className="window-dots">
                      <span></span><span></span><span></span>
                    </div>
                    <span className="header-title">Analytics Summary</span>
                  </div>
                  <div className="preview-card-body">
                    <div className="analytics-grid">
                      <div className="analytics-stat-card">
                        <div className="stat-icon bg-primary-subtle"><i className="bi bi-people text-primary"></i></div>
                        <div>
                          <div className="stat-number">12,547</div>
                          <div className="stat-name">Visitors</div>
                        </div>
                        <span className="stat-change positive">+18%</span>
                      </div>
                      <div className="analytics-stat-card">
                        <div className="stat-icon bg-success-subtle"><i className="bi bi-eye text-success"></i></div>
                        <div>
                          <div className="stat-number">48,293</div>
                          <div className="stat-name">Page Views</div>
                        </div>
                        <span className="stat-change positive">+24%</span>
                      </div>
                      <div className="analytics-stat-card">
                        <div className="stat-icon bg-warning-subtle"><i className="bi bi-clock text-warning"></i></div>
                        <div>
                          <div className="stat-number">3m 42s</div>
                          <div className="stat-name">Avg. Duration</div>
                        </div>
                        <span className="stat-change positive">+8%</span>
                      </div>
                      <div className="analytics-stat-card">
                        <div className="stat-icon bg-info-subtle"><i className="bi bi-arrow-return-left text-info"></i></div>
                        <div>
                          <div className="stat-number">32.4%</div>
                          <div className="stat-name">Bounce Rate</div>
                        </div>
                        <span className="stat-change negative">-5%</span>
                      </div>
                    </div>
                    <div className="analytics-chart-preview">
                      <div className="chart-title">Page Views & Visitors Trend</div>
                      <svg viewBox="0 0 400 120" preserveAspectRatio="none" className="trend-chart">
                        <defs>
                          <linearGradient id="blueGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.4"/>
                            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0"/>
                          </linearGradient>
                          <linearGradient id="greenGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#10b981" stopOpacity="0.4"/>
                            <stop offset="100%" stopColor="#10b981" stopOpacity="0"/>
                          </linearGradient>
                        </defs>
                        <path d="M0,80 Q50,70 100,55 T200,45 T300,35 T400,20 L400,120 L0,120 Z" fill="url(#blueGradient)"/>
                        <path d="M0,80 Q50,70 100,55 T200,45 T300,35 T400,20" fill="none" stroke="#3b82f6" strokeWidth="2"/>
                        <path d="M0,90 Q50,85 100,75 T200,65 T300,55 T400,45 L400,120 L0,120 Z" fill="url(#greenGradient)"/>
                        <path d="M0,90 Q50,85 100,75 T200,65 T300,55 T400,45" fill="none" stroke="#10b981" strokeWidth="2"/>
                      </svg>
                      <div className="chart-legend">
                        <span><span className="legend-dot bg-primary"></span> Page Views</span>
                        <span><span className="legend-dot bg-success"></span> Visitors</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Uptime Monitoring Preview */}
          <div className="preview-block mb-5">
            <div className="row align-items-center">
              <div className="col-lg-5 mb-4 mb-lg-0">
                <div className="preview-content">
                  <div className="preview-icon bg-info-subtle">
                    <i className="bi bi-activity text-info"></i>
                  </div>
                  <h3>24/7 Uptime Monitoring</h3>
                  <p>Never miss a beat. Monitor your website's availability around the clock with instant alerts when issues occur.</p>
                  <ul className="preview-features-list">
                    <li><i className="bi bi-check2"></i> Response time tracking</li>
                    <li><i className="bi bi-check2"></i> Uptime percentage</li>
                    <li><i className="bi bi-check2"></i> Visual status timeline</li>
                  </ul>
                </div>
              </div>
              <div className="col-lg-7">
                <div className="preview-dashboard-card monitoring-dashboard">
                  <div className="preview-card-header">
                    <div className="window-dots">
                      <span></span><span></span><span></span>
                    </div>
                    <span className="header-title">Monitoring Overview</span>
                  </div>
                  <div className="preview-card-body">
                    <div className="monitoring-status-bar">
                      <div className="status-indicator up">
                        <i className="bi bi-check-circle-fill"></i>
                        All Systems Operational
                      </div>
                    </div>
                    <div className="monitoring-stats-row">
                      <div className="monitoring-stat">
                        <div className="stat-circle uptime">
                          <svg viewBox="0 0 36 36">
                            <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#e5e7eb" strokeWidth="3"/>
                            <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#10b981" strokeWidth="3" strokeDasharray="99.9, 100"/>
                          </svg>
                          <div className="stat-value-circle">99.9%</div>
                        </div>
                        <div className="stat-label-below">Uptime</div>
                      </div>
                      <div className="monitoring-stat">
                        <div className="stat-number-lg">145ms</div>
                        <div className="stat-label-below">Avg Response</div>
                      </div>
                      <div className="monitoring-stat">
                        <div className="stat-number-lg text-success">2,847</div>
                        <div className="stat-label-below">Total Checks</div>
                      </div>
                    </div>
                    <div className="uptime-timeline-preview">
                      <div className="timeline-label">Last 90 checks</div>
                      <div className="timeline-bars">
                        {[...Array(45)].map((_, i) => (
                          <div key={i} className={`timeline-bar ${i === 23 || i === 24 ? 'down' : 'up'}`}></div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* AI SEO Optimizer Preview */}
          <div className="preview-block mb-5">
            <div className="row align-items-center">
              <div className="col-lg-5 mb-4 mb-lg-0">
                <div className="preview-content">
                  <div className="preview-icon" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #06b6d4 100%)', width: 52, height: 52, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
                    <i className="bi bi-stars" style={{ color: '#fff', fontSize: 22 }}></i>
                  </div>
                  <h3>AI-Powered SEO Optimizer</h3>
                  <p>Boost your search rankings with AI-generated rewrites tailored to your target keywords. Get scored across 14 SEO factors and receive actionable fixes instantly.</p>
                  <ul className="preview-features-list">
                    <li><i className="bi bi-check2"></i> 14-factor SEO health score per page</li>
                    <li><i className="bi bi-check2"></i> AI rewrites: title, meta, H1 &amp; content tips</li>
                    <li><i className="bi bi-check2"></i> Keyword rank tracking on Google &amp; Bing</li>
                    <li><i className="bi bi-check2"></i> Optimized for AI search (ChatGPT, Perplexity)</li>
                  </ul>
                </div>
              </div>
              <div className="col-lg-7">
                <div className="preview-dashboard-card" style={{ background: '#0f172a', borderRadius: 16, overflow: 'hidden', boxShadow: '0 20px 60px rgba(139,92,246,0.25)' }}>
                  <div className="preview-card-header" style={{ background: '#1e293b', borderBottom: '1px solid #334155' }}>
                    <div className="window-dots"><span></span><span></span><span></span></div>
                    <span className="header-title" style={{ color: '#e2e8f0' }}>AI SEO Optimizer</span>
                    <span style={{ background: 'linear-gradient(135deg,#8b5cf6,#06b6d4)', borderRadius: 20, padding: '2px 10px', fontSize: '0.7rem', color: '#fff', fontWeight: 600 }}>AI Powered</span>
                  </div>
                  <div className="preview-card-body" style={{ padding: '1.25rem' }}>
                    {/* Score row */}
                    <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                      {[
                        { url: 'example.com/home', score: 87, color: '#10b981' },
                        { url: 'example.com/about', score: 54, color: '#f59e0b' },
                        { url: 'example.com/services', score: 31, color: '#ef4444' },
                      ].map((p, i) => (
                        <div key={i} style={{ flex: 1, background: '#1e293b', borderRadius: 10, padding: '10px 12px', border: '1px solid #334155' }}>
                          <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.url}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ background: p.color + '22', color: p.color, fontWeight: 800, fontSize: '0.85rem', padding: '2px 8px', borderRadius: 6 }}>{p.score}</span>
                            <span style={{ fontSize: '0.62rem', color: '#64748b' }}>/100</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* AI suggestion card */}
                    <div style={{ background: '#1e293b', borderRadius: 12, padding: '1rem', border: '1px solid #334155', marginBottom: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                        <i className="bi bi-stars" style={{ color: '#8b5cf6', fontSize: 14 }}></i>
                        <span style={{ color: '#c4b5fd', fontSize: '0.75rem', fontWeight: 600 }}>AI Suggestions · example.com/services</span>
                      </div>
                      {[
                        { label: 'Title', old: 'Our Services | Example', newVal: 'Top IT Services in Bahrain — Expert Solutions' },
                        { label: 'H1', old: 'Services', newVal: 'Enterprise IT Services Built for Bahrain Businesses' },
                      ].map(f => (
                        <div key={f.label} style={{ marginBottom: 8 }}>
                          <span style={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>{f.label}</span>
                          <div style={{ fontSize: '0.72rem', color: '#ef4444', textDecoration: 'line-through', opacity: 0.7 }}>{f.old}</div>
                          <div style={{ fontSize: '0.72rem', color: '#4ade80', fontWeight: 600 }}>{f.newVal}</div>
                        </div>
                      ))}
                    </div>
                    {/* Keyword ranks */}
                    <div style={{ display: 'flex', gap: 8 }}>
                      {[
                        { kw: 'IT company Bahrain', g: '#3', b: '#5' },
                        { kw: 'cloud services Bahrain', g: '#7', b: '#9' },
                      ].map((k, i) => (
                        <div key={i} style={{ flex: 1, background: '#1e293b', borderRadius: 8, padding: '8px 10px', border: '1px solid #334155' }}>
                          <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginBottom: 4 }}>{k.kw}</div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <span style={{ fontSize: '0.7rem', background: '#166534', color: '#4ade80', padding: '1px 7px', borderRadius: 4 }}>G {k.g}</span>
                            <span style={{ fontSize: '0.7rem', background: '#1e3a5f', color: '#60a5fa', padding: '1px 7px', borderRadius: 4 }}>B {k.b}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Security Scanning Preview */}
          <div className="preview-block">
            <div className="row align-items-center flex-lg-row-reverse">
              <div className="col-lg-5 mb-4 mb-lg-0">
                <div className="preview-content">
                  <div className="preview-icon bg-danger-subtle">
                    <i className="bi bi-shield-check text-danger"></i>
                  </div>
                  <h3>Security Scanning</h3>
                  <p>Protect your assets with automated security scans. Detect vulnerabilities before they become problems.</p>
                  <ul className="preview-features-list">
                    <li><i className="bi bi-check2"></i> Vulnerability detection</li>
                    <li><i className="bi bi-check2"></i> Security score tracking</li>
                    <li><i className="bi bi-check2"></i> Issue severity breakdown</li>
                  </ul>
                </div>
              </div>
              <div className="col-lg-7">
                <div className="preview-dashboard-card security-dashboard">
                  <div className="preview-card-header">
                    <div className="window-dots">
                      <span></span><span></span><span></span>
                    </div>
                    <span className="header-title">Security Summary</span>
                  </div>
                  <div className="preview-card-body">
                    <div className="security-overview">
                      <div className="security-score-circle">
                        <svg viewBox="0 0 120 120">
                          <circle cx="60" cy="60" r="54" fill="none" stroke="#e5e7eb" strokeWidth="8"/>
                          <circle cx="60" cy="60" r="54" fill="none" stroke="#10b981" strokeWidth="8" strokeLinecap="round" strokeDasharray="282" strokeDashoffset="25" transform="rotate(-90 60 60)"/>
                        </svg>
                        <div className="score-value">92</div>
                        <div className="score-label">Security Score</div>
                      </div>
                      <div className="security-issues-grid">
                        <div className="issue-card critical">
                          <span className="issue-count">0</span>
                          <span className="issue-label">Critical</span>
                        </div>
                        <div className="issue-card high">
                          <span className="issue-count">2</span>
                          <span className="issue-label">High</span>
                        </div>
                        <div className="issue-card medium">
                          <span className="issue-count">5</span>
                          <span className="issue-label">Medium</span>
                        </div>
                        <div className="issue-card low">
                          <span className="issue-count">8</span>
                          <span className="issue-label">Low</span>
                        </div>
                      </div>
                    </div>
                    <div className="vulnerability-list-preview">
                      <div className="vuln-item">
                        <span className="vuln-severity high">HIGH</span>
                        <span className="vuln-name">Missing Security Headers</span>
                        <span className="vuln-status open">Open</span>
                      </div>
                      <div className="vuln-item">
                        <span className="vuln-severity medium">MEDIUM</span>
                        <span className="vuln-name">SSL Certificate Expiry</span>
                        <span className="vuln-status resolved">Resolved</span>
                      </div>
                      <div className="vuln-item">
                        <span className="vuln-severity low">LOW</span>
                        <span className="vuln-name">HTTP to HTTPS Redirect</span>
                        <span className="vuln-status open">Open</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted By Section */}
      {clients.length > 0 && (
        <section className="trusted-by-section py-5">
          <div className="container">
            <div className="text-center mb-4">
              <p className="text-muted text-uppercase letter-spacing-2 mb-3">Trusted by leading companies</p>
            </div>
            <div className="client-logos-carousel">
              <div className="client-logos-track">
                {[...clients, ...clients].map((client, index) => (
                  <div key={`${client.id}-${index}`} className="client-logo-item">
                    {client.website_url ? (
                      <a href={client.website_url} target="_blank" rel="noopener noreferrer">
                        <img src={client.logo_url} alt={client.name} title={client.name} />
                      </a>
                    ) : (
                      <img src={client.logo_url} alt={client.name} title={client.name} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section id="features" className="features-section py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="section-title">Everything You Need</h2>
            <p className="section-subtitle">
              Powerful features to monitor and manage your digital infrastructure
            </p>
          </div>

          <div className="row g-4">
            {[
              {
                icon: 'bi-broadcast',
                title: 'Live Visitor Tracking',
                description: 'See who\'s on your site right now with real-time visitor data and geographic insights.',
                gradient: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
              },
              {
                icon: 'bi-graph-up-arrow',
                title: 'Advanced Analytics',
                description: 'Beautiful charts and comprehensive reports to understand your traffic patterns.',
                gradient: 'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
              },
              {
                icon: 'bi-activity',
                title: 'Uptime Monitoring',
                description: '24/7 monitoring with instant alerts when your site goes down.',
                gradient: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
              },
              {
                icon: 'bi-shield-lock',
                title: 'Security Scanning',
                description: 'Automated vulnerability detection and security recommendations.',
                gradient: 'linear-gradient(135deg, #ef4444 0%, #f59e0b 100%)',
              },
              {
                icon: 'bi-stars',
                title: 'AI SEO Optimizer',
                description: 'Score pages across 14 SEO factors and get AI-generated titles, meta descriptions, H1s and content tips tailored to your keywords.',
                gradient: 'linear-gradient(135deg, #8b5cf6 0%, #06b6d4 100%)',
              },
              {
                icon: 'bi-search-heart',
                title: 'Keyword Rank Tracking',
                description: 'Track your target keywords on Google & Bing. Optimized for AI search engines like ChatGPT and Perplexity.',
                gradient: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              },
            ].map((feature, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <div className="feature-card" style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="feature-icon" style={{ background: feature.gradient }}>
                    <i className={`bi ${feature.icon}`}></i>
                  </div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="how-it-works-section py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="section-title">Get Started in Minutes</h2>
            <p className="section-subtitle">Simple setup, powerful results</p>
          </div>

          <div className="row g-4">
            <div className="col-lg-3 col-md-6">
              <div className="step-card">
                <div className="step-number">1</div>
                <h4>Create Account</h4>
                <p>Sign up for free and create your organization profile</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="step-card">
                <div className="step-number">2</div>
                <h4>Add Assets</h4>
                <p>Register your websites, apps, and digital assets</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="step-card">
                <div className="step-number">3</div>
                <h4>Integrate</h4>
                <p>Copy and paste our simple tracking code</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="step-card">
                <div className="step-number">4</div>
                <h4>Rank &amp; Grow</h4>
                <p>Monitor analytics, track keywords, and let AI optimize your SEO</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section py-5" style={{ background: '#f8fafc' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle">
              Everything you need to know about CloudAyn's analytics, uptime monitoring, and AI SEO tools
            </p>
          </div>
          <div className="row justify-content-center">
            <div className="col-lg-9">
              <div className="accordion" id="faqAccordion">

                {[
                  {
                    q: 'What is the best uptime monitoring tool for websites and apps?',
                    a: 'CloudAyn is one of the best uptime monitoring tools available for websites and apps. It monitors your digital assets around the clock — checking availability every minute and sending instant email alerts when your site goes down. You get a full visual timeline of incidents, response time tracking, uptime percentage reports, and historical performance data — all on a single dashboard. Unlike standalone monitoring tools, CloudAyn also combines analytics, security scanning, and AI SEO in one platform, eliminating the need for multiple subscriptions.',
                  },
                  {
                    q: 'Is CloudAyn a good AI SEO tool for ranking higher on Google?',
                    a: 'Yes — CloudAyn is an AI-driven SEO optimizer purpose-built for 2025 and 2026. It crawls your pages, scores each one across 14 on-page SEO factors (title tag, meta description, H1 headings, canonical URLs, Open Graph tags, JSON-LD structured data, image alt texts, content length, HTTPS, viewport, internal links, Twitter Card, robots meta, and heading hierarchy), then uses Claude AI to generate keyword-optimised rewrites for your title, meta description, H1, and content. You also receive tips to help your pages appear in AI search engines like ChatGPT, Perplexity, and Google SGE.',
                  },
                  {
                    q: 'What makes CloudAyn the best website visitor analytics tool?',
                    a: 'CloudAyn gives you real-time visitor tracking, live visitor counts with geographic mapping, device and browser breakdowns, traffic source analysis, page-level performance metrics, bounce rate, average session duration, and year-over-year comparisons. It\'s a privacy-friendly Google Analytics alternative with a lightweight tracking script. Unlike most analytics-only tools, CloudAyn also shows you uptime status, security vulnerabilities, and SEO health — all from one dashboard. No data sampling, no 24-hour data delays, no hidden limits.',
                  },
                  {
                    q: 'How does CloudAyn\'s AI-driven SEO optimizer work?',
                    a: 'CloudAyn\'s AI SEO tool works in three steps: (1) It crawls your tracked pages and scores them on 14 ranking factors. (2) It identifies critical, warning, and informational issues for each factor. (3) It sends your page content and issues to AI, which returns optimised rewrites for your title tag (50–60 chars), meta description (150–160 chars), and H1 — all incorporating your target keywords naturally. You also receive 3 content tips and 2 AI search tips. Keyword rank positions on Google and Bing are tracked automatically.',
                  },
                  {
                    q: 'Can I track keyword rankings on Google and Bing with CloudAyn?',
                    a: 'Yes. CloudAyn\'s SERP rank tracker lets you add target keywords per website, then automatically checks your ranking position on both Google and Bing. You\'ll see your keyword position, the exact URL that ranked, and how your positions change over time. This helps you measure the impact of your SEO improvements and prioritise which pages and keywords need attention.',
                  },
                  {
                    q: 'Does CloudAyn offer a free uptime monitoring and analytics plan?',
                    a: 'Yes. CloudAyn\'s free plan includes 24/7 uptime monitoring, real-time website analytics, live visitor tracking, and basic security scanning for one website — no credit card required. Paid plans start from $29/month and unlock multiple websites, AI SEO analysis, keyword rank tracking, server monitoring, and team member access. All plans include a free trial.',
                  },
                  {
                    q: 'How is CloudAyn different from other website analytics tools?',
                    a: 'Most analytics tools only track visitor data. CloudAyn combines real-time analytics, 24/7 uptime monitoring, security vulnerability scanning, server infrastructure monitoring, and a full AI SEO optimizer — all in one platform. You manage every aspect of your website\'s performance and visibility from a single dashboard without juggling multiple subscriptions. CloudAyn is also privacy-focused, GDPR-compliant, and significantly lighter than traditional analytics scripts.',
                  },
                  {
                    q: 'Does CloudAyn work with WordPress, Shopify, and other platforms?',
                    a: 'Yes — CloudAyn works with any website or web app regardless of platform. It supports WordPress, Shopify, Wix, Squarespace, Webflow, React, Vue, Angular, Next.js, plain HTML, and Node.js apps. You add a single lightweight JavaScript snippet to your site and CloudAyn handles the rest. For server monitoring, you install the open-source CloudAyn agent on any Linux server.',
                  },
                ].map((item, i) => (
                  <div className="accordion-item border mb-2 rounded overflow-hidden" key={i}>
                    <h3 className="accordion-header" id={`faq-h-${i}`}>
                      <button
                        className="accordion-button collapsed fw-medium"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#faq-c-${i}`}
                        aria-expanded="false"
                        aria-controls={`faq-c-${i}`}
                        style={{ fontSize: '0.95rem' }}
                      >
                        {item.q}
                      </button>
                    </h3>
                    <div
                      id={`faq-c-${i}`}
                      className="accordion-collapse collapse"
                      aria-labelledby={`faq-h-${i}`}
                      data-bs-parent="#faqAccordion"
                    >
                      <div className="accordion-body text-muted" style={{ lineHeight: 1.75 }}>
                        {item.a}
                      </div>
                    </div>
                  </div>
                ))}

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog / Insights Section */}
      {posts.length > 0 && (
        <section id="blog" className="blog-section py-5">
          <div className="container">
            <div className="d-flex justify-content-between align-items-center mb-5">
              <div>
                <h2 className="section-title mb-1">Latest Insights</h2>
                <p className="section-subtitle mb-0">Tips, guides and updates on analytics, monitoring &amp; SEO</p>
              </div>
              <Link to="/blog" className="btn btn-outline-primary">
                All Posts <i className="bi bi-arrow-right ms-1"></i>
              </Link>
            </div>
            <div className="row g-4">
              {posts.map((post) => (
                <div key={post.id} className="col-lg-4 col-md-6">
                  <Link to={`/blog/${post.slug}`} className="text-decoration-none">
                    <div className="blog-card h-100">
                      {post.cover_image_url ? (
                        <img
                          src={post.cover_image_url}
                          alt={post.cover_image_alt || post.title}
                          className="blog-card-img"
                          loading="lazy"
                        />
                      ) : (
                        <div className="blog-card-img-placeholder">
                          <i className="bi bi-file-earmark-text"></i>
                        </div>
                      )}
                      <div className="blog-card-body">
                        {post.tags && (
                          <div className="blog-card-tags mb-2">
                            {post.tags.split(',').slice(0, 2).map((t) => (
                              <span key={t} className="blog-tag">{t.trim()}</span>
                            ))}
                          </div>
                        )}
                        <h3 className="blog-card-title">{post.title}</h3>
                        {post.excerpt && <p className="blog-card-excerpt">{post.excerpt}</p>}
                        <div className="blog-card-meta">
                          <span>{post.author_name}</span>
                          {post.published_at && (
                            <span>{new Date(post.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Section */}
      <section id="contact" className="contact-section py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2 className="section-title">Ready to Get Started?</h2>
              <p className="section-subtitle mb-4">
                Join companies who trust CloudAyn to monitor, secure, and grow their digital presence with AI-powered SEO
              </p>
              <div className="contact-info">
                <div className="contact-item">
                  <i className="bi bi-envelope"></i>
                  <span>support@cloudayn.com</span>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="contact-form-card">
                <h4 className="mb-4">Send us a message</h4>
                <form>
                  <div className="mb-3">
                    <input type="text" className="form-control" placeholder="Your Name" />
                  </div>
                  <div className="mb-3">
                    <input type="email" className="form-control" placeholder="Your Email" />
                  </div>
                  <div className="mb-3">
                    <textarea className="form-control" rows={4} placeholder="Your Message"></textarea>
                  </div>
                  <button type="submit" className="btn btn-primary w-100">Send Message</button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="row">
            {/* Brand */}
            <div className="col-lg-5 mb-4 mb-lg-0">
              <div className="d-flex align-items-center mb-3">
                <div className="logo-icon">
                  <span className="logo-text">C</span>
                </div>
                <span className="brand-name ms-2">CloudAyn</span>
              </div>
              <p className="text-muted" style={{ maxWidth: 340 }}>
                Monitor, analyze, rank, and secure your digital assets — all in one AI-powered platform.
              </p>
              <div className="mt-3">
                <a href="mailto:support@cloudayn.com" className="text-muted small me-3" style={{ textDecoration: 'none' }}>
                  <i className="bi bi-envelope me-1"></i>support@cloudayn.com
                </a>
              </div>
            </div>

            {/* Product */}
            <div className="col-lg-2 col-md-3 col-6 mb-4 mb-lg-0">
              <h6>Product</h6>
              <ul className="list-unstyled">
                <li><a href="#features">Features</a></li>
                <li><a href="#preview">Preview</a></li>
                <li><a href="#faq">FAQ</a></li>
                <li><a href="#contact">Contact</a></li>
              </ul>
            </div>

            {/* Resources */}
            <div className="col-lg-2 col-md-3 col-6 mb-4 mb-lg-0">
              <h6>Resources</h6>
              <ul className="list-unstyled">
                <li><Link to="/blog">Blog &amp; Insights</Link></li>
                <li><a href="/signup">Get Started Free</a></li>
                <li><a href="/login">Login</a></li>
                <li><a href="/sitemap.xml" target="_blank" rel="noopener noreferrer">Sitemap</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div className="col-lg-3 col-md-3 col-6">
              <h6>Legal</h6>
              <ul className="list-unstyled">
                <li><a href="/privacy">Privacy Policy</a></li>
                <li><a href="/terms">Terms &amp; Conditions</a></li>
                <li><a href="/refund-policy">Refund Policy</a></li>
              </ul>
            </div>
          </div>
          <hr className="my-4" />
          <div className="row align-items-center">
            <div className="col-md-6 text-center text-md-start mb-3 mb-md-0">
              <p className="mb-0 text-muted">&copy; 2026 CloudAyn by INFIYO TECHNOLOGIES WLL. All rights reserved.</p>
            </div>
            <div className="col-md-6 text-center text-md-end">
              <a href="/terms" className="text-muted me-3" style={{ fontSize: '0.85rem', textDecoration: 'none' }}>Terms</a>
              <a href="/privacy" className="text-muted me-3" style={{ fontSize: '0.85rem', textDecoration: 'none' }}>Privacy</a>
              <a href="/refund-policy" className="text-muted" style={{ fontSize: '0.85rem', textDecoration: 'none' }}>Refunds</a>
            </div>
          </div>
          <div className="text-center mt-3">
            <span style={{ fontSize: '0.8rem', color: '#9ca3af', letterSpacing: '0.02em' }}>
              Made with{' '}
              <span style={{
                display: 'inline-block',
                color: '#e53e3e',
                animation: 'bh-heartbeat 1.8s ease-in-out infinite',
              }}>❤️</span>
              {' '}in Bahrain 🇧🇭
            </span>
            <style>{`
              @keyframes bh-heartbeat {
                0%, 100% { transform: scale(1); opacity: 1; }
                14%       { transform: scale(1.4); }
                28%       { transform: scale(1); }
                42%       { transform: scale(1.3); }
                70%       { transform: scale(1); opacity: 0.7; }
              }
            `}</style>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
